import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, SkipForward, SkipBack, X, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";

interface SpotifyWebPlayerProps {
  accessToken: string;
  playlistId: string;
  onClose: () => void;
}

interface WebPlaybackState {
  paused: boolean;
  position: number;
  duration: number;
  track_window: {
    current_track: {
      name: string;
      artists: { name: string }[];
      album: {
        name: string;
        images: { url: string }[];
      };
    };
  };
}

declare global {
  interface Window {
    Spotify: {
      Player: new (config: {
        name: string;
        getOAuthToken: (cb: (token: string) => void) => void;
        volume: number;
      }) => SpotifyPlayerInstance;
    };
    onSpotifyWebPlaybackSDKReady: () => void;
  }
}

interface SpotifyPlayerInstance {
  addListener: (event: string, cb: (data: unknown) => void) => void;
  connect: () => Promise<boolean>;
  disconnect: () => void;
  togglePlay: () => Promise<void>;
  previousTrack: () => Promise<void>;
  nextTrack: () => Promise<void>;
  getCurrentState: () => Promise<WebPlaybackState | null>;
  _options: { id: string };
}

export default function SpotifyWebPlayer({
  accessToken,
  playlistId,
  onClose,
}: SpotifyWebPlayerProps) {
  const { t } = useTranslation();
  const playerRef = useRef<SpotifyPlayerInstance | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTrack, setCurrentTrack] = useState<{
    name: string;
    artist: string;
    albumArt: string;
  } | null>(null);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const positionInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPlaylistId = useRef<string | null>(null);

  // Load Spotify SDK script
  useEffect(() => {
    if (document.getElementById("spotify-sdk-script")) return;

    const script = document.createElement("script");
    script.id = "spotify-sdk-script";
    script.src = "https://sdk.scdn.co/spotify-player.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Initialize player
  useEffect(() => {
    const initPlayer = () => {
      const player = new window.Spotify.Player({
        name: "My EyeControl Player",
        getOAuthToken: (cb) => cb(accessToken),
        volume: 0.5,
      });

      player.addListener("ready", (data: unknown) => {
        const { device_id } = data as { device_id: string };
        setDeviceId(device_id);
        setIsReady(true);
      });

      player.addListener("player_state_changed", (state: unknown) => {
        if (!state) return;
        const s = state as WebPlaybackState;
        setIsPaused(s.paused);
        setPosition(s.position);
        setDuration(s.duration);
        const track = s.track_window.current_track;
        setCurrentTrack({
          name: track.name,
          artist: track.artists.map((a) => a.name).join(", "),
          albumArt: track.album.images[0]?.url || "",
        });
      });

      player.addListener("not_ready", () => setIsReady(false));

      player.connect();
      playerRef.current = player;
    };

    if (window.Spotify) {
      initPlayer();
    } else {
      window.onSpotifyWebPlaybackSDKReady = initPlayer;
    }

    return () => {
      if (positionInterval.current) clearInterval(positionInterval.current);
      playerRef.current?.disconnect();
    };
  }, [accessToken]);

  // Start playlist when device is ready or playlist changes
  useEffect(() => {
    if (!deviceId || !isReady || !playlistId) return;
    if (prevPlaylistId.current === playlistId) return;
    prevPlaylistId.current = playlistId;

    fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        context_uri: `spotify:playlist:${playlistId}`,
      }),
    }).catch(() => {});
  }, [deviceId, isReady, playlistId, accessToken]);

  // Track position updates
  useEffect(() => {
    if (positionInterval.current) clearInterval(positionInterval.current);
    if (!isPaused) {
      positionInterval.current = setInterval(() => {
        setPosition((p) => Math.min(p + 1000, duration));
      }, 1000);
    }
    return () => {
      if (positionInterval.current) clearInterval(positionInterval.current);
    };
  }, [isPaused, duration]);

  const formatTime = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, "0")}`;
  };

  const handlePlayPause = useCallback(() => {
    playerRef.current?.togglePlay();
  }, []);

  const handlePrev = useCallback(() => {
    playerRef.current?.previousTrack();
  }, []);

  const handleNext = useCallback(() => {
    playerRef.current?.nextTrack();
  }, []);

  if (!isReady) {
    return (
      <div className="mb-6 p-6 rounded-xl bg-white/5 flex items-center justify-center gap-3">
        <div className="w-6 h-6 border-2 border-[#1DB954] border-t-transparent rounded-full animate-spin" />
        <span className="text-white text-lg">{t("connectingToPlayer")}</span>
      </div>
    );
  }

  return (
    <div className="mb-6 p-4 rounded-xl bg-white/5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white">{t("nowPlaying")}</h2>
        <Button
          onClick={onClose}
          data-scan-item
          className="h-14 px-8 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-lg"
        >
          <X className="w-5 h-5 mr-2" />
          {t("close")}
        </Button>
      </div>

      {/* Track info */}
      <div className="flex items-center gap-4 mb-6">
        {currentTrack?.albumArt && (
          <img
            src={currentTrack.albumArt}
            alt={currentTrack.name}
            className="w-20 h-20 rounded-lg object-cover"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-lg truncate">
            {currentTrack?.name || "—"}
          </h3>
          <p className="text-gray-400 text-base truncate">
            {currentTrack?.artist || "—"}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3 mb-6">
        <span className="text-gray-400 text-sm w-12 text-left">
          {formatTime(position)}
        </span>
        <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#1DB954] rounded-full transition-all"
            style={{ width: duration > 0 ? `${(position / duration) * 100}%` : "0%" }}
          />
        </div>
        <span className="text-gray-400 text-sm w-12 text-right">
          {formatTime(duration)}
        </span>
      </div>

      {/* Big accessible controls */}
      <div className="flex items-center justify-center gap-4">
        <Button
          onClick={handlePrev}
          data-scan-item
          className="h-16 w-16 rounded-full bg-white/10 hover:bg-white/20 text-white p-0"
          aria-label={t("previousTrack")}
        >
          <SkipBack className="w-8 h-8" />
        </Button>
        <Button
          onClick={handlePlayPause}
          data-scan-item
          className="h-20 w-20 rounded-full bg-[#1DB954] hover:bg-[#1ed760] text-black p-0"
          aria-label={isPaused ? t("play") : t("pause")}
        >
          {isPaused ? (
            <Play className="w-10 h-10 fill-black ml-1" />
          ) : (
            <Pause className="w-10 h-10 fill-black" />
          )}
        </Button>
        <Button
          onClick={handleNext}
          data-scan-item
          className="h-16 w-16 rounded-full bg-white/10 hover:bg-white/20 text-white p-0"
          aria-label={t("nextTrack")}
        >
          <SkipForward className="w-8 h-8" />
        </Button>
      </div>
    </div>
  );
}
